import { Trans } from '@lingui/react/macro';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { type KeyboardEvent, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { fetchProfilePoints, profilePointsQueryKey } from '../../../features/points/hooks/useProfilePoints.ts';
import useAppStore from '../../../store/rootStore';
import { getValidNonce } from '../../../store/slices/authSlice.ts';
import { DESKTOP_BREAKPOINT } from '../constants';
import { hoverMenuCardBaseSx } from '../hover_menu_surface.tsx';
import { isRewardsPath, REWARDS_MENU_DETAILS_ENABLED } from '../rewards_menu/config.tsx';
import { RewardsPanel } from '../rewards_menu/rewards_panel.tsx';
import { useFilteredRewardsMenuItems } from '../rewards_menu/use_filtered_rewards_menu_items.ts';
import { ToolsPanel } from '../tools_menu/tools_panel.tsx';
import { useHoverMenu } from '../use_hover_menu.ts';
import { useFilteredNavItems } from '../useFilteredNavItems.ts';
import { NavHoverTrigger, navHoverTriggerEndIcon } from './nav_trigger.tsx';

export type NavHoverPanelId = 'rewards' | 'tools';

const PANEL_WIDTH = 280;
// Rewards pane is wider only while it carries the v2 data panel.
const REWARDS_PANEL_WIDTH = REWARDS_MENU_DETAILS_ENABLED ? 520 : PANEL_WIDTH;
const MOVE_MS = 320;
const MOVE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const PANEL_PADDING = 'var(--mui-spacing)';
const PANEL_ITEM_GAP = 'calc(0.5 * var(--mui-spacing))';
const MOVE_TRANSITION = `transform ${MOVE_MS}ms ${MOVE_EASE}, width ${MOVE_MS}ms ${MOVE_EASE}, height ${MOVE_MS}ms ${MOVE_EASE}`;

const GroupRoot = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  [theme.breakpoints.down(DESKTOP_BREAKPOINT)]: {
    display: 'none',
  },
}));

const FloatingShell = styled(Box)({
  position: 'absolute',
  top: '100%',
  left: 0,
  zIndex: 'calc(var(--mui-zIndex-appBar) + 1)',
  paddingTop: 'var(--mui-spacing)',
  pointerEvents: 'none',
  willChange: 'transform',
});

const MenuCard = styled(Box)({
  ...hoverMenuCardBaseSx,
  width: '100%',
  overflow: 'hidden',
  transformOrigin: 'top center',
  willChange: 'opacity, transform, height',
});

const ContentTrack = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start', // panes keep their own height so the card can resize between them
  width: 'max-content', // panes have unequal widths; slide by pixel offsets
  willChange: 'transform',
});

const ContentPane = styled(Box)({
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: PANEL_ITEM_GAP,
  padding: PANEL_PADDING,
  boxSizing: 'border-box',
});

export const NavHoverGroup = memo(function NavHoverGroup() {
  const { pathname } = useLocation();
  const groupRef = useRef<HTMLDivElement | null>(null);
  const rewardsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const toolsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const rewardsPaneRef = useRef<HTMLDivElement | null>(null);
  const toolsPaneRef = useRef<HTMLDivElement | null>(null);
  const armSlideRafRef = useRef<number | null>(null);

  const rewardsItems = useFilteredRewardsMenuItems();
  const filteredNavItems = useFilteredNavItems();
  const toolsItems = useMemo(() => filteredNavItems.filter((item) => item.overflow), [filteredNavItems]);

  const showRewards = rewardsItems.length > 0;
  const showTools = toolsItems.length > 0;

  const [panel, setPanel] = useState<NavHoverPanelId>(showRewards ? 'rewards' : 'tools');
  const [geometry, setGeometry] = useState({ left: 0, width: PANEL_WIDTH, height: 0 });
  const [slideEnabled, setSlideEnabled] = useState(false);

  const queryClient = useQueryClient();
  const authNonce = useAppStore((state) => getValidNonce(state.authNonce)?.value);
  const isFullyAuthorized = useAppStore((state) => state.isFullyAuthorized());
  const pointsEnabled = useAppStore((state) => state.config?.features?.pointsEnabled);

  const prefetchNavMenus = useCallback(() => {
    if (!REWARDS_MENU_DETAILS_ENABLED) return;
    if (!isFullyAuthorized || !authNonce || !pointsEnabled) return;
    void queryClient.prefetchQuery({
      queryKey: profilePointsQueryKey(authNonce),
      queryFn: () => fetchProfilePoints(authNonce),
      staleTime: 60_000,
    });
  }, [authNonce, isFullyAuthorized, pointsEnabled, queryClient]);

  const { open, mounted, menuId, handleEnter, handleLeave, handleToggle, handleClose, handleEscape } = useHoverMenu({
    onPrefetch: prefetchNavMenus,
  });

  const rewardsActive = isRewardsPath(pathname);
  const toolsActive = useMemo(
    () => toolsItems.some((item) => !item.external && (item.path === pathname || pathname.startsWith(`${item.path}/`))),
    [pathname, toolsItems]
  );

  const cancelArmSlide = useCallback(() => {
    if (armSlideRafRef.current === null) return;
    cancelAnimationFrame(armSlideRafRef.current);
    armSlideRafRef.current = null;
  }, []);

  const activate = useCallback(
    (id: NavHoverPanelId) => {
      setPanel(id);
      handleEnter();
    },
    [handleEnter]
  );

  const syncGeometry = useCallback(() => {
    const group = groupRef.current;
    const trigger = panel === 'rewards' ? rewardsTriggerRef.current : toolsTriggerRef.current;
    const pane = panel === 'rewards' ? rewardsPaneRef.current : toolsPaneRef.current;
    if (!group || !trigger) return;

    const groupRect = group.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const width = panel === 'rewards' ? REWARDS_PANEL_WIDTH : PANEL_WIDTH;
    // Center under the trigger, but keep the wider rewards card from running off-screen left.
    const centered = triggerRect.left - groupRect.left + (triggerRect.width - width) / 2;
    const left = Math.max(centered, 8 - groupRect.left);
    const height = pane?.offsetHeight ?? 0;

    setGeometry((prev) =>
      prev.left === left && prev.width === width && prev.height === height ? prev : { left, width, height }
    );
  }, [panel]);

  useLayoutEffect(() => {
    if (!open) {
      cancelArmSlide();
      setSlideEnabled(false);
      return;
    }
    syncGeometry();
  }, [cancelArmSlide, open, syncGeometry]);

  // Keep the card height tracking the active pane as its content resizes.
  useEffect(() => {
    if (!mounted || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => syncGeometry());
    if (rewardsPaneRef.current) observer.observe(rewardsPaneRef.current);
    if (toolsPaneRef.current) observer.observe(toolsPaneRef.current);
    return () => observer.disconnect();
  }, [mounted, syncGeometry]);

  // Open snaps into place; transitions arm afterwards for panel switches.
  useEffect(() => {
    if (!open) return;
    cancelArmSlide();
    armSlideRafRef.current = requestAnimationFrame(() => {
      armSlideRafRef.current = requestAnimationFrame(() => {
        armSlideRafRef.current = null;
        setSlideEnabled(true);
      });
    });
    return cancelArmSlide;
  }, [cancelArmSlide, open]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleEscape((panel === 'rewards' ? rewardsTriggerRef : toolsTriggerRef).current);
      }
    },
    [handleEscape, panel]
  );

  const handleTriggerToggle = useCallback(
    (id: NavHoverPanelId) => {
      if (open && panel === id) {
        handleToggle();
        return;
      }
      activate(id);
    },
    [activate, handleToggle, open, panel]
  );

  if (!showRewards && !showTools) return null;

  const trackX = !showRewards || (showTools && panel === 'tools') ? -REWARDS_PANEL_WIDTH : 0;
  const moveTransition = slideEnabled ? MOVE_TRANSITION : 'none';

  return (
    <GroupRoot ref={groupRef} onKeyDown={handleKeyDown} onMouseLeave={handleLeave}>
      {showRewards ? (
        <NavHoverTrigger
          ref={rewardsTriggerRef}
          highlighted={rewardsActive || (open && panel === 'rewards')}
          isOpen={open && panel === 'rewards'}
          onMouseEnter={() => activate('rewards')}
          onClick={() => handleTriggerToggle('rewards')}
          endIcon={navHoverTriggerEndIcon}
          aria-haspopup="true"
          aria-expanded={open && panel === 'rewards'}
          aria-controls={menuId}
          disableRipple
        >
          <Trans comment="Header Rewards dropdown trigger">Rewards</Trans>
        </NavHoverTrigger>
      ) : null}

      {showTools ? (
        <NavHoverTrigger
          ref={toolsTriggerRef}
          highlighted={toolsActive || (open && panel === 'tools')}
          isOpen={open && panel === 'tools'}
          onMouseEnter={() => activate('tools')}
          onClick={() => handleTriggerToggle('tools')}
          endIcon={navHoverTriggerEndIcon}
          aria-haspopup="true"
          aria-expanded={open && panel === 'tools'}
          aria-controls={menuId}
          disableRipple
        >
          <Trans comment="Header Tools dropdown trigger">Tools</Trans>
        </NavHoverTrigger>
      ) : null}

      <FloatingShell
        id={menuId}
        aria-hidden={!open}
        onMouseEnter={handleEnter}
        sx={{
          width: geometry.width,
          transform: `translate3d(${geometry.left}px, 0, 0)`,
          pointerEvents: open ? 'auto' : 'none',
          transition: moveTransition,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <MenuCard
          data-open={open}
          sx={{
            height: geometry.height > 0 ? geometry.height : undefined,
            opacity: open ? 1 : 0,
            transform: open ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.96)',
            visibility: open || geometry.height > 0 ? 'visible' : 'hidden',
            transition: slideEnabled
              ? `opacity 200ms cubic-bezier(0.16, 1, 0.3, 1), transform 240ms cubic-bezier(0.16, 1, 0.3, 1), height ${MOVE_MS}ms ${MOVE_EASE}`
              : 'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1), transform 240ms cubic-bezier(0.16, 1, 0.3, 1)',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          <ContentTrack
            sx={{
              transform: `translate3d(${trackX}px, 0, 0)`,
              transition: slideEnabled ? `transform ${MOVE_MS}ms ${MOVE_EASE}` : 'none',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            {mounted ? (
              <>
                <ContentPane ref={rewardsPaneRef} sx={{ width: REWARDS_PANEL_WIDTH }}>
                  {showRewards ? <RewardsPanel open={open && panel === 'rewards'} onNavigate={handleClose} /> : null}
                </ContentPane>
                <ContentPane ref={toolsPaneRef} sx={{ width: PANEL_WIDTH }}>
                  {showTools ? <ToolsPanel open={open && panel === 'tools'} onNavigate={handleClose} /> : null}
                </ContentPane>
              </>
            ) : null}
          </ContentTrack>
        </MenuCard>
      </FloatingShell>
    </GroupRoot>
  );
});
